import { useReducedMotion } from "@mantine/hooks";
import { useEffect, useMemo, useRef } from "react";
import { useSnapshot } from "valtio";
import { useIsDark } from "@/hooks/useIsDark";
import { i18nStore } from "@/store/i18n";
import { isWebglOrbVariant, toOrbParamRecord } from "@/types/ai-assistant-orb";
import type { AssistantOrbProps } from "../assistant-orb.types";
import { idleBrightness, isIdleFrozen, shouldAnimateOrb } from "../orb-motion";
import { resolveOrbColors } from "../orb-theme";
import {
	deriveLiquidGlassStops,
	toLiquidGlassUniforms,
} from "./orb-webgl-palette";
import {
	registerWebglOrbInstance,
	type WebglOrbInstanceData,
	type WebglOrbInstanceState,
	type WebglOrbRect,
	webglShaderKindFor,
} from "./orb-webgl-stage";

/**
 * Sisi visible orb liquid-glass: elemen TRANSPARAN yang hanya menandai posisi
 * & ukuran di layar. Piksel sungguhan digambar oleh satu kanvas overlay
 * bersama (orb-webgl-stage.ts) sebagai sprite lewat viewport+scissor — bukan
 * oleh elemen ini sendiri. Hanya dirender saat AssistantOrb (dispatcher)
 * sudah memastikan WebGL2 tersedia; fallback-nya AssistantCanvas2dOrb.
 */
export function AssistantOrbWebgl({
	config,
	name,
	size,
	idleMs = 0,
	forceLight,
}: AssistantOrbProps) {
	const elRef = useRef<HTMLSpanElement>(null);
	const reducedMotion = useReducedMotion();
	const { animasiTransisi } = useSnapshot(i18nStore);
	const themeIsLight = !useIsDark();
	const light = forceLight ?? themeIsLight;

	const colors = resolveOrbColors(config, light);
	const colorStops = useMemo(
		() => deriveLiquidGlassStops(colors.from, colors.to),
		[colors.from, colors.to],
	);
	const uniforms = useMemo(
		() =>
			toLiquidGlassUniforms(toOrbParamRecord(config.variant, config.params)),
		[config.variant, config.params],
	);

	const animate = shouldAnimateOrb(reducedMotion, animasiTransisi);
	const frozen = isIdleFrozen(idleMs);
	const brightness = idleBrightness(idleMs);

	const kind = isWebglOrbVariant(config.variant)
		? webglShaderKindFor(config.variant)
		: "liquid-glass";

	// Dibaca ULANG tiap frame oleh panggung bersama — lihat komentar serupa di
	// AssistantCanvas2dOrb; sama-sama menghindari daftar ulang tiap render.
	// liquid-morph: palet TETAP, jadi tak membawa warna/parameter — hanya tema.
	const stateRef = useRef<WebglOrbInstanceData>(
		kind === "liquid-morph"
			? { kind, light, animate, frozen, brightness }
			: { kind, colorStops, uniforms, animate, frozen, brightness },
	);
	stateRef.current =
		kind === "liquid-morph"
			? { kind, light, animate, frozen, brightness }
			: { kind, colorStops, uniforms, animate, frozen, brightness };

	// biome-ignore lint/correctness/useExhaustiveDependencies: `kind` sengaja jadi pemicu daftar-ulang — panggung memilih program dari getState().kind SAAT mendaftar, jadi pindah jenis shader (mis. glass → morph) wajib mendaftar ulang walau efek tak membaca `kind` langsung
	useEffect(() => {
		const el = elRef.current;
		if (!el) return;

		const getRect = (): WebglOrbRect | null => {
			const rect = el.getBoundingClientRect();
			if (rect.width <= 0 || rect.height <= 0) return null;
			return {
				x: rect.left,
				y: rect.top,
				width: rect.width,
				height: rect.height,
			};
		};

		return registerWebglOrbInstance(
			() => ({ ...stateRef.current, getRect }) as WebglOrbInstanceState,
		);
		// Daftar ulang HANYA bila jenis shader berganti (admin mengganti varian
		// antar-jenis): config/warna/idle berubah dibaca lewat stateRef, bukan
		// dependency — mendaftar ulang tiap geser slider akan memasang-lepas
		// sprite tanpa perlu.
	}, [kind]);

	const style = {
		width: size,
		height: size,
		display: "block",
		flexShrink: 0,
	} as const;

	return (
		<span
			ref={elRef}
			role="img"
			aria-label={name}
			data-assistant-orb="webgl"
			style={style}
		/>
	);
}
